import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Divider,
  Pagination,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AttachMoney as SalaryIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';
import { Visibility as VisibilityIcon, Send as SendIcon } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SearchAndFilter from '../../components/search/SearchAndFilter';
import ApiService from '../../services/api';
import { isBoostActive, boostCardSx } from '../../utils/boost';
import { useCurrency } from '../../context/CurrencyContext';

const JobListings = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const locationHook = useLocation();
  const [filters, setFilters] = useState({
    location: '',
    jobType: '',
    employmentType: '',
    experienceLevel: '',
    category: '',
    salary: '',
    salaryRange: [0, 100000],
    datePosted: '',
    employerId: ''
  });
  const [sortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [savedJobs, setSavedJobs] = useState([]);
  const { formatCurrency } = useCurrency();
  const { currentUser, userRole, userCapabilities } = useAuth();
  const DEFAULT_SALARY_MAX = 100000;
  
  const jobsPerPage = 10;

  // Removed salaryLabels; dynamic search uses numeric ranges

  // Load jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError('');
        
        const params = {
          page,
          limit: jobsPerPage,
          sortBy: sortBy === 'newest' ? 'createdAt' : sortBy,
          sortOrder: sortBy === 'newest' ? 'desc' : 'asc',
          ...filters
        };

        // Map frontend filter names to backend expected names
        const empType = (filters.employmentType || filters.jobType || '').toString();
        if (empType) {
          params.employmentType = empType.toLowerCase().replace(/\s+/g, '-');
        }
        const expLevel = (filters.experienceLevel || '').toString();
        if (expLevel) {
          params.experienceLevel = expLevel.toLowerCase().replace(/\s+/g, '-');
        }
        // Salary range mapping if available
        if (Array.isArray(filters.salaryRange)) {
          const [min, max] = filters.salaryRange;
          // Only send bounds when user actually adjusts away from defaults
          if (typeof min === 'number' && min > 0) params.salaryMin = min;
          if (typeof max === 'number' && max > 0 && max < DEFAULT_SALARY_MAX) params.salaryMax = max;
        }

        // Remove empty filter values
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await ApiService.getJobs(params);
        
        if (response.success) {
          // Base list from API
          let list = response.jobs || [];

          // Client-side fallback filter: ensure employerId constraint is honored
          if (filters.employerId) {
            const target = String(filters.employerId);
            list = list.filter(job => {
              const candidates = [
                job.employer?._id,
                job.employer?.id,
                job.employerId,
                job.employer,
                job.postedBy,
                job.userId,
                job.ownerId
              ].filter(Boolean).map(v => String(v));
              return candidates.includes(target);
            });
          }

          // Sort boosted first, then featured, then newest
          const isBoostActiveLocal = (j) => !!j?.boosted && (!j?.boostExpiresAt || new Date(j.boostExpiresAt) > new Date());
          const sorted = [...list].sort((a, b) => {
            const aBoost = isBoostActiveLocal(a);
            const bBoost = isBoostActiveLocal(b);
            if (aBoost !== bBoost) return aBoost ? -1 : 1;
            if (!!a?.featured !== !!b?.featured) return a?.featured ? -1 : 1;
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          setJobs(sorted);
        } else {
          throw new Error(response.message || 'Failed to fetch jobs');
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setError('Failed to load jobs. Please try again later.');
        // Fallback to empty array if API fails
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [page, sortBy, filters]);

  // Read employerId from query params on mount / when location changes
  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);
    const employerId = params.get('employerId') || '';
    if (employerId && employerId !== filters.employerId) {
      setFilters(prev => ({ ...prev, employerId }));
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHook.search]);

  // removed duplicate fetchJobs (useEffect above handles fetching)

  // Fetch saved jobs for the current user
  useEffect(() => {
    const fetchSavedJobs = async () => {
      if (!currentUser || !currentUser._id) return;
      
      try {
        const response = await ApiService.getSavedJobs(currentUser._id);
        if (response.success) {
          const savedJobIds = response.savedJobs.map(job => job.jobId);
          setSavedJobs(savedJobIds);
        }
      } catch (err) {
        console.error('Error fetching saved jobs:', err);
      }
    };

    fetchSavedJobs();
  }, [currentUser]);

  // Handle filter change
  const handleFilterChange = useCallback((name, value) => {
    // Avoid unnecessary state updates when value hasn't changed
    let changed = false;
    setFilters(prevFilters => {
      const prevValue = prevFilters[name];
      const isEqual = Array.isArray(prevValue) && Array.isArray(value)
        ? prevValue.length === value.length && prevValue.every((v, i) => v === value[i])
        : prevValue === value;
      if (isEqual) return prevFilters;
      changed = true;
      return { ...prevFilters, [name]: value };
    });
    if (changed) setPage(1); // Reset to first page when filters actually change
  }, []);

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Toggle job bookmark/save
  const toggleSaveJob = async (jobId) => {
    if (!currentUser) {
      // Redirect to login or show login prompt
      return;
    }
    
    try {
      // Toggle saved state
      if (savedJobs.includes(jobId)) {
        setSavedJobs(savedJobs.filter(id => id !== jobId));
        // TODO: Remove from database via API
      } else {
        setSavedJobs([...savedJobs, jobId]);
        // TODO: Add to database via API
      }
    } catch (error) {
      console.error('Error toggling job save:', error);
    }
  };

  // Optimistically track apply click and bump local counter
  const trackApplyAndBump = (jobId) => {
    if (!jobId) return;
    // Optimistic UI bump first for instant feedback
    setJobs(prev => prev.map(j => {
      const jid = j._id || j.id;
      if (String(jid) === String(jobId)) {
        return { ...j, applyClicks: (j.applyClicks ?? j.applicationCount ?? 0) + 1 };
      }
      return j;
    }));
    // Then reconcile with server-sourced count so everyone sees the same number
    Promise.resolve(ApiService.trackJobApplyClick(jobId))
      .then((serverCount) => {
        if (typeof serverCount === 'number' && serverCount >= 0) {
          setJobs(prev => prev.map(j => {
            const jid = j._id || j.id;
            if (String(jid) === String(jobId)) {
              return { ...j, applyClicks: serverCount };
            }
            return j;
          }));
        }
      })
      .catch(() => {
        // Non-blocking: if server fails, keep optimistic value
      });
  };

  const handleSearchResults = useCallback(async (searchParams) => {
    try {
      setLoading(true);
      setError('');
      
      // Extract and normalize filters for backend
      const f = searchParams.filters || {};
      const backendParams = {
        page: 1,
        limit: jobsPerPage,
        sortBy,
        // Map UI filters to backend expected keys
        query: (searchParams.query || '').trim(),
        category: f.category || '',
        location: f.location || '',
        employmentType: f.employmentType
          ? f.employmentType.toLowerCase().replace(/\s+/g, '-')
          : '',
        experienceLevel: f.experienceLevel
          ? f.experienceLevel.toLowerCase().replace(/\s+/g, '-')
          : ''
      };

      // Salary range mapping (jobs only)
      if (Array.isArray(f.salaryRange)) {
        const [min, max] = f.salaryRange;
        // Only include salaryMax when it's been reduced from the default upper bound
        if (typeof min === 'number' && min > 0) backendParams.salaryMin = min;
        if (typeof max === 'number' && max > 0 && max < DEFAULT_SALARY_MAX) backendParams.salaryMax = max;
      }

      // Remove empty values
      Object.keys(backendParams).forEach((key) => {
        const v = backendParams[key];
        if (v === '' || v === null || v === undefined) {
          delete backendParams[key];
        }
      });

      const response = await ApiService.searchJobs(backendParams);
      // Apply employer filter fallback to search results as well
      let searchList = response.jobs || [];
      if (filters.employerId) {
        const target = String(filters.employerId);
        searchList = searchList.filter(job => {
          const candidates = [
            job.employer?._id,
            job.employer?.id,
            job.employerId,
            job.employer,
            job.postedBy,
            job.userId,
            job.ownerId
          ].filter(Boolean).map(v => String(v));
          return candidates.includes(target);
        });
      }
      setJobs(searchList);
      setPage(1);
      
      // Sync local filters state to UI but preserve employerId and any other existing keys
      setFilters(prev => ({
        ...prev,
        location: f.location || '',
        jobType: backendParams.employmentType || '',
        category: f.category || '',
        salary: '' // UI salary uses predefined ranges; keep blank after free-range search
      }));
      
      if (searchParams.query) {
        setSearchTerm(searchParams.query);
      }
    } catch (error) {
      console.error('Error searching jobs:', error);
      setError('Failed to search jobs. Please try again later.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, jobsPerPage, filters.employerId]);

  // Remove unused: handleFiltersChange (Search component uses onFiltersChange->handleFilterChange)

  // Filter and sort jobs
  const filteredJobs = Array.isArray(jobs) ? jobs
    .filter(job => {
      const normalize = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '-');
      // Employer constraint: only show jobs from selected employer
      const matchesEmployer = !filters.employerId || (() => {
        const target = String(filters.employerId);
        const candidates = [
          job.employer?._id,
          job.employer?.id,
          job.employerId,
          job.employer,
          job.postedBy,
          job.userId,
          job.ownerId
        ].filter(Boolean).map(v => String(v));
        return candidates.includes(target);
      })();

      // Search term filter
      const matchesSearch = searchTerm === '' || 
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Location filter
      const matchesLocation = filters.location === '' || 
        job.location?.toLowerCase().includes(filters.location.toLowerCase());
      
      // Job type filter
      const activeEmpType = filters.employmentType || filters.jobType || '';
      const matchesJobType = activeEmpType === '' || (
        normalize(job.employmentType) === normalize(activeEmpType) ||
        normalize(job.jobType) === normalize(activeEmpType)
      );

      // Experience level filter
      const matchesExperience = filters.experienceLevel === '' || (
        normalize(job.experienceLevel) === normalize(filters.experienceLevel)
      );
      
      // Category filter
      const matchesCategory = filters.category === '' || 
        job.category === filters.category;
      
      // Salary range filter
      const matchesSalary = (() => {
        const range = filters.salaryRange;
        if (!Array.isArray(range)) return true;
        const [min, max] = range;
        const jobMin = job.salaryMin ?? job.salary ?? 0;
        const jobMax = job.salaryMax ?? job.salary ?? jobMin;
        const applyMin = typeof min === 'number' && min > 0;
        // Treat the default upper bound as "no max" so it doesn't exclude high-salary jobs
        const applyMax = typeof max === 'number' && max > 0 && max < DEFAULT_SALARY_MAX;
        return (!applyMin || jobMax >= min) && (!applyMax || jobMin <= max);
      })();

      // Date posted filter (days)
      const matchesDatePosted = (() => {
        const daysStr = filters.datePosted;
        if (!daysStr) return true;
        const days = parseInt(daysStr, 10);
        if (!(days > 0)) return true;
        const posted = new Date(job.createdAt || job.postedDate || Date.now());
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        return posted >= cutoff;
      })();
      
      return matchesSearch && matchesLocation && matchesJobType && 
        matchesExperience && matchesCategory && matchesSalary && matchesDatePosted && matchesEmployer;
    })
    .sort((a, b) => {
      // Always prioritize boosted jobs first, then featured
      const aBoost = isBoostActive(a?.boosted, a?.boostExpiresAt) ? 1 : 0;
      const bBoost = isBoostActive(b?.boosted, b?.boostExpiresAt) ? 1 : 0;
      if (bBoost !== aBoost) return bBoost - aBoost;
      const aFeat = a?.featured ? 1 : 0;
      const bFeat = b?.featured ? 1 : 0;
      if (bFeat !== aFeat) return bFeat - aFeat;
      // Then apply selected option
      switch (sortBy) {
        case 'newest':
          return new Date(b.postedDate) - new Date(a.postedDate);
        case 'oldest':
          return new Date(a.postedDate) - new Date(b.postedDate);
        case 'salary-high':
          return (b.salaryMax || 0) - (a.salaryMax || 0);
        case 'salary-low':
          return (a.salaryMin || 0) - (b.salaryMin || 0);
        default:
          return new Date(b.createdAt || b.postedDate || 0) - new Date(a.createdAt || a.postedDate || 0);
      }
    }) : [];

  // Paginate jobs
  const paginatedJobs = filteredJobs.slice(
    (page - 1) * jobsPerPage,
    page * jobsPerPage
  );

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Job Listings
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Browse through our curated list of job opportunities
          </Typography>
        </Box>
        {(userRole === 'employer' || userRole === 'admin' || userCapabilities?.canPostJobs) && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/employer/post-job"
            sx={{ minWidth: 150 }}
          >
            Add Job
          </Button>
        )}
      </Box>

      {/* Search and Filter Section */}
      <SearchAndFilter
        searchType="jobs"
        onSearchResults={handleSearchResults}
        onFiltersChange={handleFilterChange}
        initialFilters={{
          location: filters.location,
          category: filters.category,
          employmentType: filters.jobType
        }}
      />

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Results Count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              {filteredJobs.length} jobs found
            </Typography>
          </Box>

          {/* Job Listings */}
          {paginatedJobs.length > 0 ? (
            <Grid container spacing={3}>
              {paginatedJobs.map((job) => {
                const boostActive = isBoostActive(job?.boosted, job?.boostExpiresAt);
                const highlight = boostActive;
                return (
                <Grid item xs={12} key={job._id || job.id}>
                  <Card sx={{ display: 'flex', flexDirection: 'column', ...(highlight ? boostCardSx : {}) }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h5" component="h2" gutterBottom>
                            {job.title}
                          </Typography>
                          <Box sx={{ mb: 1 }}>
                            {boostActive && (
                              <Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {/* Employer logo with fallback */}
                            <Avatar
                              src={(job.employer?.companyLogo || job.companyLogoUrl || job.companyLogo || job.logo || null) || '/logo192.png'}
                              sx={{ width: 24, height: 24, mr: 1 }}
                            />
                            <Typography variant="subtitle1" color="text.secondary">
                              {job.companyName || job.company}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {(() => {
                                const normalizeCountry = (c) => (c || '').replace(/\bUSA\b/i, 'Zambia');
                                if (typeof job.location === 'object') {
                                  const city = job.location.city || '';
                                  const country = normalizeCountry(job.location.country || '');
                                  return `${city}${city && country ? ', ' : ''}${country}`.replace(/^,\s*|,\s*/g, '');
                                }
                                const asString = job.location || 'N/A';
                                return asString.replace(/\bUSA\b/i, 'Zambia');
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <SalaryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {job.salary?.min && job.salary?.max 
                                ? `${formatCurrency(job.salary.min)} - ${formatCurrency(job.salary.max)}${job.salary.period ? ` per ${job.salary.period}` : ''}`
                                : job.salary?.min 
                                  ? `${formatCurrency(job.salary.min)}+${job.salary.period ? ` per ${job.salary.period}` : ''}`
                                  : 'Salary not specified'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <IconButton 
                            onClick={() => toggleSaveJob(job._id)}
                            color="primary"
                            disabled={!currentUser}
                            title={currentUser ? 'Save job' : 'Login to save jobs'}
                          >
                            {savedJobs.includes(job._id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" paragraph>
                          {job.description?.substring(0, 200)}...
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label={job.jobType || 'full-time'} size="small" />
                        <Chip label={job.category || 'Technology'} size="small" />
                        <Chip label={job.workLocation || 'on-site'} size="small" color="primary" variant="outlined" />
                        <Chip label={job.experienceLevel || 'mid-level'} size="small" color="secondary" variant="outlined" />
                        {job.requirements?.skills?.slice(0, 2).map((skill, index) => (
                          <Chip key={`skill-${job._id}-${index}`} label={skill} size="small" variant="outlined" />
                        ))}
                        {job.requirements?.skills?.length > 2 && (
                          <Chip label={`+${job.requirements.skills.length - 2} more skills`} size="small" variant="outlined" />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Posted: {job.createdAt ? formatDate(job.createdAt) : 'Recently'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {job.applicationDeadline && (
                              `Deadline: ${formatDate(job.applicationDeadline)}`
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {(job.views ?? job.viewCount ?? 0)} views
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SendIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {(job.applyClicks ?? job.applicationCount ?? 0)} apply clicks
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                    <Divider />
                      <CardActions>
                        <Button 
                          component={RouterLink} 
                          to={`/jobs/${job._id || ''}`}
                          variant="contained"
                        >
                          View Details
                        </Button>
                        {(userRole === 'jobseeker' || userRole === 'general') && (
                          <Button 
                            variant="outlined"
                            component={RouterLink}
                            to={userRole === 'general' ? '/wizard/job-seeker' : `/jobs/${job._id || ''}/apply`}
                            state={userRole === 'general' ? { 
                              returnTo: `/jobs/${job._id || job.id || ''}`,
                              message: 'Complete your profile to apply for this job',
                              targetRole: 'job_seeker'
                            } : undefined}
                            onClick={() => {
                              // Ensure we send a valid Mongo ObjectId to backend
                              const id = job._id;
                              trackApplyAndBump(id);
                            }}
                          >
                            {userRole === 'general' ? 'Complete Profile to Apply' : 'Apply Now'}
                          </Button>
                        )}
                      </CardActions>
                  </Card>
                </Grid>
              )})}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {filters.employerId ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No jobs posted yet by this employer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please check back later. This employer has not posted any jobs yet.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No jobs found matching your criteria
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search filters or check back later for new opportunities
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Pagination */}
          {filteredJobs.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination 
                count={Math.ceil(filteredJobs.length / jobsPerPage)} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default JobListings;